import { useState, useEffect } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Table, TableHeader, TableBody, TableHead, TableRow, TableCell } from '@/components/ui/table'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog'
import { updateSaleItemPacked, updateSaleItemQuantity, fetchSalesWithDateRange } from '../supabase-client'
import { parseDateString, toLocalDateString } from '../utils/dateUtils'
import InlineQuantityInput from './sales-timeline/InlineQuantityInput'
import CoverSheetModal from './CoverSheetModal'

const getToday = () => toLocalDateString(new Date())

export default function PackingModal({ isOpen, onClose, onRefresh, updateStatus, onItemQuantityUpdate }) {
  const [todaysOrders, setTodaysOrders] = useState([])
  const [updatingItems, setUpdatingItems] = useState(new Set())
  const [sortConfig, setSortConfig] = useState({ key: 'customer_name', direction: 'asc' })
  const [isCoverSheetModalOpen, setIsCoverSheetModalOpen] = useState(false)
  const [loading, setLoading] = useState(false)
  const [selectedDate, setSelectedDate] = useState(getToday)
  const [editingQuantities, setEditingQuantities] = useState({})

  const isToday = selectedDate === getToday()

  const navigateDate = (direction) => {
    setSelectedDate(prev => {
      const d = new Date(prev + 'T12:00:00')
      d.setDate(d.getDate() + (direction === 'next' ? 1 : -1))
      return toLocalDateString(d)
    })
  }

  // Reset to today when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedDate(getToday())
      setEditingQuantities({})
    }
  }, [isOpen])

  // Fetch pending and packed sales for selected date
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false

    async function loadSales() {
      setLoading(true)
      try {
        const rawSales = await fetchSalesWithDateRange(selectedDate, selectedDate)
        if (cancelled) return

        const pending = (rawSales || [])
          .filter(sale => sale.status === 'pending' || sale.status === 'packed')
          .map(sale => ({
            ...sale,
            customer_name: sale.customer?.name || 'Unknown',
            branch_name: sale.branch?.name || '',
            customer_type: sale.branch?.customer_type?.name || '',
            items: (sale.sale_items || []).map(item => ({
              ...item,
              strain: item.product?.strain || 'Unknown',
              productTypeName: item.product_type?.name || ''
            }))
          }))

        setTodaysOrders(pending)
      } catch (err) {
        console.error('[PackingModal] Error fetching sales:', err)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadSales()
    return () => { cancelled = true }
  }, [isOpen, selectedDate])

  const formatDate = (dateString) => {
    const date = parseDateString(dateString)
    return date.toLocaleDateString('en-GB', {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      year: '2-digit'
    })
  }

  // Flatten orders to item-based structure for table display
  const getPackingItems = () => {
    const items = []
    todaysOrders.forEach(sale => {
      sale.items.forEach(item => {
        items.push({
          ...item,
          saleId: sale.id,
          saleStatus: sale.status,
          customer_name: sale.customer_name,
          branch_name: sale.branch_name,
          customer_type: sale.customer_type,
          consignment_no: sale.consignment_no,
          date: sale.date
        })
      })
    })
    return items
  }

  // Sort items based on current sort configuration
  const getSortedItems = () => {
    const items = getPackingItems()
    return items.sort((a, b) => {
      let aValue = a[sortConfig.key]
      let bValue = b[sortConfig.key]

      // Handle item_number sorting (numeric, nulls to end)
      if (sortConfig.key === 'item_number') {
        // Handle null item numbers - they should sort to the end
        if (!aValue && !bValue) return 0
        if (!aValue) return sortConfig.direction === 'asc' ? 1 : -1
        if (!bValue) return sortConfig.direction === 'asc' ? -1 : 1
        aValue = parseInt(aValue)
        bValue = parseInt(bValue)
      }

      // Handle string sorting
      if (typeof aValue === 'string') {
        aValue = aValue.toLowerCase()
        bValue = bValue.toLowerCase()
      }

      if (aValue < bValue) return sortConfig.direction === 'asc' ? -1 : 1
      if (aValue > bValue) return sortConfig.direction === 'asc' ? 1 : -1
      return 0
    })
  }

  // Handle sorting
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }))
  }

  // Get sort icon
  const getSortIcon = (key) => {
    if (sortConfig.key !== key) return '↕'
    return sortConfig.direction === 'asc' ? '↑' : '↓'
  }

  // Get row color based on packing status
  const getRowColor = (item) => {
    // Find all items for this customer
    // Group by consignment_no if available, otherwise by saleId (for unpacked sales)
    const customerItems = getPackingItems().filter(i => {
      const sameCustomer = i.customer_name === item.customer_name
      if (item.consignment_no) {
        return sameCustomer && i.consignment_no === item.consignment_no
      } else {
        // For sales without consignment_no, group by saleId
        return sameCustomer && i.saleId === item.saleId
      }
    })
    
    const packedCount = customerItems.filter(i => i.packed).length
    const totalCount = customerItems.length
    
    if (packedCount === 0) {
      // No items packed - red
      return { backgroundColor: '#fee2e2', borderLeft: '4px solid #ef4444' }
    } else if (packedCount === totalCount) {
      // All items packed - green  
      return { backgroundColor: '#d1fae5', borderLeft: '4px solid #10b981' }
    } else {
      // Some items packed - yellow
      return { backgroundColor: '#fef3c7', borderLeft: '4px solid #fbbf24' }
    }
  }

  const handleItemPackedChange = async (saleId, itemId, packed) => {
    setUpdatingItems(prev => new Set(prev).add(itemId))

    try {
      // updateSaleItemPacked returns the updated item with item_number
      const updatedItem = await updateSaleItemPacked(itemId, packed)

      // Build updated orders to use for both state and status check
      const updatedOrders = todaysOrders.map(sale => {
        if (sale.id === saleId) {
          return {
            ...sale,
            items: sale.items.map(item =>
              item.id === itemId
                ? { ...item, packed, item_number: updatedItem.item_number }
                : item
            )
          }
        }
        return sale
      })

      setTodaysOrders(updatedOrders)

      // Auto-promote/demote sale status based on tray number allocation
      const sale = updatedOrders.find(s => s.id === saleId)
      if (sale && updateStatus) {
        const allHaveTrayNumbers = sale.items.every(i => i.item_number != null)
        if (allHaveTrayNumbers && sale.status === 'pending') {
          updateStatus(`sale-${saleId}`, 'packed')
          // Update local status so the sale stays visible as packed
          setTodaysOrders(prev => prev.map(s =>
            s.id === saleId ? { ...s, status: 'packed' } : s
          ))
        } else if (!allHaveTrayNumbers && sale.status === 'packed') {
          updateStatus(`sale-${saleId}`, 'pending')
          setTodaysOrders(prev => prev.map(s =>
            s.id === saleId ? { ...s, status: 'pending' } : s
          ))
        }
      }

      // Refresh parent component data
      onRefresh?.()
    } catch (error) {
      console.error('Error updating packed status:', error)
    } finally {
      setUpdatingItems(prev => {
        const newSet = new Set(prev)
        newSet.delete(itemId)
        return newSet
      })
    }
  }

  const handleQuantityCommit = async (saleId, itemId, newQuantity) => {
    // Find current saved quantity to check if anything actually changed
    const currentItem = todaysOrders
      .flatMap(s => s.items)
      .find(i => i.id === itemId)
    if (currentItem && currentItem.quantity === newQuantity) {
      // No change — clear editing state and skip DB call
      setEditingQuantities(prev => {
        const next = { ...prev }
        delete next[itemId]
        return next
      })
      return
    }

    const previousOrders = todaysOrders

    // Optimistically update local modal state
    setTodaysOrders(prev => prev.map(sale => {
      if (sale.id === saleId) {
        return {
          ...sale,
          items: sale.items.map(item =>
            item.id === itemId ? { ...item, quantity: newQuantity } : item
          )
        }
      }
      return sale
    }))

    // Clear editing state for this item
    setEditingQuantities(prev => {
      const next = { ...prev }
      delete next[itemId]
      return next
    })

    try {
      await updateSaleItemQuantity(itemId, newQuantity)
      // Optimistically update the timeline behind the modal
      onItemQuantityUpdate?.(saleId, itemId, newQuantity)
      onRefresh?.()
    } catch (error) {
      console.error('Error updating quantity:', error)
      setTodaysOrders(previousOrders)
    }
  }

  if (!isOpen) return null

  const totalOrders = todaysOrders.length
  const pendingOrders = todaysOrders.filter(s => s.status === 'pending').length
  const packedOrders = todaysOrders.filter(s => s.status === 'packed').length
  const packingItems = getPackingItems()
  const totalItems = packingItems.length
  const packedItems = packingItems.filter(item => item.packed).length
  
  // Calculate totals
  const getTotalValue = () => {
    return packingItems.reduce((sum, item) => sum + (item.price * item.quantity), 0)
  }
  
  const getTotalWeight = () => {
    return packingItems.reduce((sum, item) => sum + item.quantity, 0)
  }

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent 
        className="odin-modal-medium"
        style={{
          backgroundColor: 'white',
          border: '1px solid #e5e7eb',
          borderRadius: '0.5rem',
          boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)'
        }}
      >
        <DialogHeader>
          <DialogTitle>📦 {isToday ? "Today's Packing Sheet" : 'Packing Sheet'}</DialogTitle>
          <DialogDescription>
            {totalOrders} order{totalOrders !== 1 ? 's' : ''}{pendingOrders > 0 && packedOrders > 0 ? ` (${pendingOrders} pending, ${packedOrders} packed)` : ''} • {packedItems}/{totalItems} items packed
          </DialogDescription>
        </DialogHeader>

        {/* Date navigation */}
        <div className="flex items-center justify-center gap-2">
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigateDate('prev')}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <span className="min-w-36 text-center text-sm font-medium">
            {formatDate(selectedDate)}
          </span>
          <Button variant="outline" size="icon" className="h-8 w-8" onClick={() => navigateDate('next')}>
            <ChevronRight className="h-4 w-4" />
          </Button>
          {!isToday && (
            <Button variant="outline" size="sm" onClick={() => setSelectedDate(getToday())}>
              Today
            </Button>
          )}
        </div>

        <div className="py-4">
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="odin-spinner h-6 w-6" />
              <span className="ml-2 text-muted-foreground">Loading orders...</span>
            </div>
          ) : todaysOrders.length === 0 ? (
            <div className="odin-empty">
              <div className="odin-empty-icon">📦</div>
              <h3 className="odin-empty-title">No orders</h3>
              <p className="odin-empty-description">
                No orders scheduled for packing{isToday ? ' today' : ` on ${formatDate(selectedDate)}`}.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              {/* Packing Table */}
              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead onClick={() => handleSort('item_number')} className="cursor-pointer hover:bg-muted/50 w-28">
                        Tray # {getSortIcon('item_number')}
                      </TableHead>
                      <TableHead onClick={() => handleSort('customer_name')} className="cursor-pointer hover:bg-muted/50 w-56">
                        Name {getSortIcon('customer_name')}
                      </TableHead>
                      <TableHead onClick={() => handleSort('strain')} className="cursor-pointer hover:bg-muted/50">
                        Item {getSortIcon('strain')}
                      </TableHead>
                      <TableHead className="w-20 text-right">Qty</TableHead>
                      <TableHead className="w-12 text-center">✓</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {getSortedItems().map(item => (
                      <TableRow
                        key={`${item.saleId}-${item.id}`}
                        style={getRowColor(item)}
                      >
                        <TableCell>
                          <div className="font-mono text-sm">
                            {item.item_number || <span className="text-muted-foreground italic">—</span>}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">
                            {item.customer_name}
                            {item.branch_name && item.branch_name !== 'Main' && (
                              <span className="text-muted-foreground font-normal"> {item.branch_name}</span>
                            )}
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">{item.strain}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <InlineQuantityInput
                            value={editingQuantities[item.id] ?? item.quantity}
                            onChange={(val) => setEditingQuantities(prev => ({ ...prev, [item.id]: val }))}
                            onBlur={(val) => handleQuantityCommit(item.saleId, item.id, val)}
                            disabled={item.saleStatus === 'packed' || updatingItems.has(item.id)}
                          />
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-2">
                            <Checkbox
                              id={`pack-${item.id}`}
                              checked={item.packed}
                              onCheckedChange={(checked) => handleItemPackedChange(item.saleId, item.id, checked)}
                              disabled={item.saleStatus === 'packed' || updatingItems.has(item.id)}
                            />
                            {updatingItems.has(item.id) && (
                              <div className="odin-spinner h-3 w-3" />
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Totals */}
              <div className="flex justify-end">
                <div className="text-right">
                  <span className="text-lg font-bold">Total: £{getTotalValue().toFixed(2)} ({getTotalWeight().toFixed(1)}kg)</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button 
            variant="outline" 
            onClick={() => setIsCoverSheetModalOpen(true)}
            disabled={todaysOrders.length === 0}
          >
            Cover Sheets
          </Button>
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Cover Sheet Modal */}
    <CoverSheetModal 
      isOpen={isCoverSheetModalOpen}
      onClose={() => setIsCoverSheetModalOpen(false)}
      todaysOrders={todaysOrders}
    />
    </>
  )
}